'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useSyncExternalStore } from 'react';
import { getMonolithApiHeaders } from '@udemy-v2/api-fetch';
import { useCommonData } from '@udemy-v2/common-data';
import { useIsOrgHost } from '@/lib/hooks/use-is-org-host';
export interface OrgBranding {
  name?: string;
  logoUrl?: string;
  isAnonymous: boolean;
}
interface BrandContextResponse {
  me?: {
    id?: number | null;
  };
  brand?: {
    title?: string;
    logo_url_2x?: string;
    organization?: false | {
      has_logo?: boolean;
    };
  };
}
const BRAND_CONTEXT_URL = '/api-2.0/contexts/me/?brand=true&me=true';

// `undefined` until the request settles; `null` when the host has no organization or the call failed.
let snapshot: OrgBranding | null | undefined;
let requested = false;
const listeners = new Set<() => void>();
const setSnapshot = (next: OrgBranding | null) => {
  snapshot = next;
  listeners.forEach(listener => listener());
};
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
const getSnapshot = () => snapshot;
const getServerSnapshot = () => undefined;
async function requestOrgBranding() {
  if (requested) {
    return;
  }
  requested = true;
  try {
    const response = await fetch(BRAND_CONTEXT_URL, {
      headers: {
        ...getMonolithApiHeaders()
      }
    });
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }
    const {
      brand,
      me
    }: BrandContextResponse = await response.json();
    setSnapshot(brand?.organization ? {
      name: brand.title,
      logoUrl: brand.organization.has_logo ? brand.logo_url_2x : undefined,
      isAnonymous: !!me && !me.id
    } : null);
  } catch (error) {
    console.error('Failed to fetch organization branding:', error);
    setSnapshot(null);
  }
}
export const resetOrgBrandingForTests = () => {
  snapshot = undefined;
  requested = false;
  listeners.clear();
};

// Anonymous visitors never get `commonData.organization`, and ISR output can't depend on the
// request host, so branding for org hosts is resolved client-side from the monolith's brand context.
export const useOrgBranding = () => {
  const $ = _c(3);
  const commonData = useCommonData();
  const isOrgHost = useIsOrgHost();
  const enabled = isOrgHost && !commonData?.page?.isLoading && !commonData?.user && !commonData?.organization;
  let t0;
  let t1;
  if ($[0] !== enabled) {
    t0 = () => {
      if (enabled) {
        requestOrgBranding();
      }
    };
    t1 = [enabled];
    $[0] = enabled;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  const branding = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return enabled ? branding ?? undefined : undefined;
};